// ─── Unfinished parts of a finished build ─────────────────────────────────────
// services/verify.js checks every site before it is saved and the repair pass
// fixes what it can. What survives reaches the workspace as `issues`, written
// for the engineer reading the logs: "#featured-grid is empty and nothing fills
// it, so that section renders as a blank band."
//
// The workspace used to answer that with one grey line — "3 sections still
// need work — tell me what they should show" — which named nothing, so the
// person reading it had nothing to tell. This turns each finding into two
// things: a few words the site owner recognises ("Shop: the product grid is
// empty"), and a precise request the model can act on, which the "Fix these"
// button writes into the message box for them to send or edit. Nothing is sent
// on their behalf — an automatic second pass once spent a credit and broke the
// hero image, which is why it is theirs to send.

import { pageLabel } from '@/lib/sitePages';

/** "featured-grid" → "featured grid"; "quickView" → "quick view". */
function humanise(id) {
  return String(id || '')
    .replace(/^#/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .trim() || 'a';
}

const on = (page) => (page ? `${pageLabel(page)}: ` : '');

/**
 * @param {{kind:string, message:string, page?:string}} issue  one verify.js finding
 * @returns {{text:string, fix:string}} what to show, and what to ask the model
 */
export function describeIssue(issue) {
  const page = issue?.page || '';
  const message = String(issue?.message || '').trim();

  switch (issue?.kind) {
    case 'empty-container': {
      const id = (message.match(/^#([\w-]+)/) || [])[1] || '';
      return {
        text: `${on(page)}the ${humanise(id)} section is empty`,
        fix: `On ${page || 'the site'}, the #${id} section is empty — fill it with real content for this business.`,
      };
    }
    case 'image-no-src': {
      const n = Number((message.match(/^(\d+)/) || [])[1]) || 1;
      return {
        text: `${on(page)}${n === 1 ? 'an image is' : `${n} images are`} missing`,
        fix: `On ${page || 'the site'}, ${n === 1 ? 'one image has' : `${n} images have`} no picture — add real images that fit each spot.`,
      };
    }
    case 'broken-link': {
      const target = (message.match(/links to (\S+?),? which/) || [])[1] || 'a page';
      return {
        text: `${on(page)}a link goes to a page that doesn't exist`,
        fix: `${page || 'A page'} links to ${target}, which the site does not have — point it at a page that exists.`,
      };
    }
    case 'dead-lookup':
      return {
        text: 'A button or menu isn’t connected yet',
        fix: `Fix the site's script so every button and menu works: ${message}`,
      };
    case 'js-syntax':
      return {
        text: 'Menus, cart and forms aren’t working yet',
        fix: `The site's JavaScript has an error, so nothing interactive works — fix it: ${message}`,
      };
    case 'form-navigates':
      return {
        text: 'A form reloads the page when it is sent',
        fix: 'Give every form a submit handler, so sending it shows a confirmation instead of reloading the page.',
      };
    default:
      return { text: message.slice(0, 90) || 'Something needs a second look', fix: message };
  }
}

/** The message the "Fix these" button writes into the composer. */
export function fixRequest(issues) {
  const lines = (issues || []).map((issue, i) => `${i + 1}. ${describeIssue(issue).fix}`);
  return ['Please finish these parts of the site, and change nothing else:', ...lines].join('\n');
}
